/* eslint-disable no-redeclare */

import {
  DefinedInitialDataInfiniteOptions,
  DefinedInitialDataOptions,
  DefinedUseInfiniteQueryResult,
  DefinedUseQueryResult,
  InfiniteData,
  QueryClient,
  QueryKey,
  UndefinedInitialDataInfiniteOptions,
  UndefinedInitialDataOptions,
  UseInfiniteQueryOptions,
  UseInfiniteQueryResult,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';

import { IApiError } from '@northladder/utilities';

declare module '@tanstack/react-query' {
  export function useQuery<
    TQueryFnData = unknown,
    TError = IApiError,
    TData = TQueryFnData,
    TQueryKey extends QueryKey = QueryKey
  >(
    options: DefinedInitialDataOptions<TQueryFnData, TError, TData, TQueryKey>,
    queryClient?: QueryClient
  ): DefinedUseQueryResult<TData, TError>;

  export function useQuery<
    TQueryFnData = unknown,
    TError = IApiError,
    TData = TQueryFnData,
    TQueryKey extends QueryKey = QueryKey
  >(
    options: UndefinedInitialDataOptions<
      TQueryFnData,
      TError,
      TData,
      TQueryKey
    >,
    queryClient?: QueryClient
  ): UseQueryResult<TData, TError>;

  export function useQuery<
    TQueryFnData = unknown,
    TError = IApiError,
    TData = TQueryFnData,
    TQueryKey extends QueryKey = QueryKey
  >(
    options: UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
    queryClient?: QueryClient
  ): UseQueryResult<TData, TError>;

  export function useInfiniteQuery<
    TQueryFnData,
    TError = IApiError,
    TData = InfiniteData<TQueryFnData>,
    TQueryKey extends QueryKey = QueryKey,
    TPageParam = unknown
  >(
    options: DefinedInitialDataInfiniteOptions<
      TQueryFnData,
      TError,
      TData,
      TQueryKey,
      TPageParam
    >,
    queryClient?: QueryClient
  ): DefinedUseInfiniteQueryResult<TData, TError>;

  export function useInfiniteQuery<
    TQueryFnData,
    TError = IApiError,
    TData = InfiniteData<TQueryFnData>,
    TQueryKey extends QueryKey = QueryKey,
    TPageParam = unknown
  >(
    options: UndefinedInitialDataInfiniteOptions<
      TQueryFnData,
      TError,
      TData,
      TQueryKey,
      TPageParam
    >,
    queryClient?: QueryClient
  ): UseInfiniteQueryResult<TData, TError>;

  export function useInfiniteQuery<
    TQueryFnData,
    TError = IApiError,
    TData = InfiniteData<TQueryFnData>,
    TQueryKey extends QueryKey = QueryKey,
    TPageParam = unknown
  >(
    options: UseInfiniteQueryOptions<
      TQueryFnData,
      TError,
      TData,
      TQueryKey,
      TPageParam
    >,
    queryClient?: QueryClient
  ): UseInfiniteQueryResult<TData, TError>;

  export function useMutation<
    TData = unknown,
    TError = IApiError,
    TVariables = void,
    TContext = unknown
  >(
    options: UseMutationOptions<TData, TError, TVariables, TContext>,
    queryClient?: QueryClient
  ): UseMutationResult<TData, TError, TVariables, TContext>;
}

/**
 * This defines a noop type that returns the type passed to it.
 * The major purpose of this is to allow for this module to re-export types
 * with a function signature that returns the type passed to it and basically
 * does nothing.
 */
export function noopType<T>(): T {
  return {} as T;
}
